import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api, can } from '../api'
import { ErrorBox, Pill } from '../components'
import { dt, num } from '../format'

export function ApiKeys() {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const admin = can(me.data, 'admin')
  const plans = useQuery({ queryKey: ['plans'], queryFn: api.plans })
  const keys = useQuery({ queryKey: ['api-keys'], queryFn: api.apiKeys, enabled: admin })
  const signups = useQuery({ queryKey: ['signups'], queryFn: api.signups, enabled: admin })
  const [form, setForm] = useState({ label: '', owner_email: '', plan: 'free', kind: 'customer' })
  const [secret, setSecret] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () => api.createKey(form),
    onSuccess: (r) => {
      setSecret(r.secret)
      setForm({ ...form, label: '', owner_email: '' })
      qc.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })
  const revoke = useMutation({ mutationFn: (id: number) => api.revokeKey(id), onSuccess: (list) => qc.setQueryData(['api-keys'], list) })

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>API keys</h1>
          <p className="sub">
            Keys for api.aleva.se. Limits are per plan in cost units (light endpoints cost 1 per request, standard 2, heavy 5); requests
            without a key get the anonymous limit per IP. A key is shown once, when it is created.
          </p>
        </div>
      </header>

      <div className="card">
        <h2>Plans</h2>
        <table className="tbl">
          <thead>
            <tr><th>Plan</th><th>Name</th><th className="num">Per minute</th><th className="num">Per day</th><th>Price</th><th>Public</th></tr>
          </thead>
          <tbody>
            {plans.data?.map((p) => (
              <tr key={p.slug}>
                <td className="mono">{p.slug}</td>
                <td>{p.name}</td>
                <td className="num">{num(p.requests_per_minute)}</td>
                <td className="num">{num(p.requests_per_day)}</td>
                <td>{p.price_note}</td>
                <td>{p.public ? 'yes' : 'no'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!admin ? (
        <p className="callout warn">Keys and signups are visible to aleva-admin only.</p>
      ) : (
        <>
          <div className="card">
            <h2>New key</h2>
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault()
                setSecret(null)
                create.mutate()
              }}
            >
              <div className="form-grid">
                <label>
                  Label
                  <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Who or what it is for" />
                </label>
                <label>
                  Owner email
                  <input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} />
                </label>
                <label>
                  Plan
                  <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                    {plans.data?.map((p) => (
                      <option key={p.slug} value={p.slug}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Kind
                  <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                    <option value="customer">customer</option>
                    <option value="site">site (aleva.se server-side)</option>
                    <option value="internal">internal</option>
                  </select>
                </label>
              </div>
              <div className="row">
                <button className="btn btn-primary" disabled={create.isPending}>
                  Create key
                </button>
              </div>
            </form>
            {create.error && <ErrorBox error={create.error} />}
            {secret && (
              <div className="callout ok">
                Copy the key now; it is not stored and cannot be shown again.
                <code className="block">{secret}</code>
                <button className="btn btn-sm" onClick={() => navigator.clipboard?.writeText(secret)}>
                  Copy
                </button>
              </div>
            )}
          </div>

          <div className="card">
            <h2>Keys</h2>
            {keys.error && <p className="callout err">{keys.error.message}</p>}
            <table className="tbl">
              <thead>
                <tr>
                  <th>Prefix</th><th>Label</th><th>Owner</th><th>Plan</th><th>Kind</th><th className="num">Requests 30 d</th><th>Last used</th>
                  <th>Created</th><th />
                </tr>
              </thead>
              <tbody>
                {keys.data?.map((k) => (
                  <tr key={k.id}>
                    <td className="mono">{k.prefix}…</td>
                    <td>{k.label}</td>
                    <td>{k.owner_email}</td>
                    <td className="mono">{k.plan}</td>
                    <td>{k.kind}</td>
                    <td className="num">{num(k.requests_30d)}</td>
                    <td>{dt(k.last_used_at)}</td>
                    <td>
                      {dt(k.created_at)} <span className="muted">{k.created_by}</span>
                    </td>
                    <td>
                      {k.revoked_at ? (
                        <Pill kind="neutral">revoked</Pill>
                      ) : (
                        <button className="btn btn-sm" disabled={revoke.isPending} onClick={() => revoke.mutate(k.id)}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card">
            <h2>Signups</h2>
            <p className="note">From the plan pages (POST /v1/signup). Keys are issued by hand for now.</p>
            <table className="tbl">
              <thead>
                <tr><th>When</th><th>Email</th><th>Plan</th><th>Page</th><th>Lang</th><th>Note</th></tr>
              </thead>
              <tbody>
                {signups.data?.map((s) => (
                  <tr key={s.id}>
                    <td>{dt(s.created_at)}</td>
                    <td>{s.email}</td>
                    <td className="mono">{s.plan}</td>
                    <td className="mono">{s.page}</td>
                    <td>{s.lang}</td>
                    <td>{s.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}
